import {
   HIGH_IMPACT_THRESHOLD,
   ScopeState,
   getButtonConfig,
   getScopeDescription,
   validateMaterialGradeId,
} from '../utils/material-grade-modal.utils';
import { MAX_GRADE_ID_LENGTH, UNASSIGNED_MATERIAL_GRADE_ID } from '../types/material-grade.constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import APIService from '../../../services/api.service';
import { PiaMaterialMappingResponse } from '../types/material-mapping.api';
import { useEscapeKey } from '../../../shared/utils/keyboard.utils';

interface MappingState {
   data: PiaMaterialMappingResponse | null;
   lastFetchedId: string | null;
}

interface UseMaterialGradeModalProps {
   visible: boolean;
   onClose: () => void;
   itemNo: string;
   familyName: string;
   materialPart: string;
   materialGrade: string;
   currentMaterialGradeId?: string;
}

export const useMaterialGradeModal = ({
   visible,
   onClose,
   itemNo,
   familyName,
   materialPart,
   materialGrade,
   currentMaterialGradeId,
}: UseMaterialGradeModalProps) => {
   const [isLoading, setIsLoading] = useState(false);
   const [mappingState, setMappingState] = useState<MappingState>({
      data: null,
      lastFetchedId: null,
   });
   const [error, setError] = useState<string | null>(null);
   const [newMaterialGradeId, setNewMaterialGradeId] = useState('');
   const [hasCheckedMapping, setHasCheckedMapping] = useState(false);
   const [inputError, setInputError] = useState<string | null>(null);
   const [scope, setScope] = useState<ScopeState>({
      type: 'item',
      filters: { family: false, part: false },
   });
   const [showDetails, setShowDetails] = useState(false);
   const [excludedItems, setExcludedItems] = useState<string[]>([]);
   const [showHighImpactWarning, setShowHighImpactWarning] = useState(false);
   const inputRef = useRef<HTMLInputElement>(null);

   useEscapeKey(onClose, visible);

   const resetMappingState = useCallback(() => {
      setHasCheckedMapping(false);
      setMappingState({ data: null, lastFetchedId: null });
      setError(null);
      setShowDetails(false);
      setExcludedItems([]);
      setShowHighImpactWarning(false);
   }, []);

   useEffect(() => {
      if (visible) {
         setNewMaterialGradeId('');
         setScope({ type: 'item', filters: { family: false, part: false } });
         setInputError(null);
         resetMappingState();

         const timer = setTimeout(() => {
            inputRef.current?.focus();
         }, 100);

         return () => clearTimeout(timer);
      }
   }, [visible, resetMappingState]);

   const isScopeSelected = useMemo(() => {
      if (scope.type === 'item' || scope.type === 'all') return true;
      return scope.filters.part;
   }, [scope]);

   const totalAffectedCount = useMemo(() => mappingState.data?.affectedItemNos?.length || null, [mappingState.data]);

   const affectedCount = useMemo(
      () => (totalAffectedCount !== null ? totalAffectedCount - excludedItems.length : null),
      [totalAffectedCount, excludedItems.length]
   );

   const totalExcludedCount = useMemo(
      () => (mappingState.data?.excludedItemNos?.length || 0) + excludedItems.length,
      [mappingState.data, excludedItems.length]
   );

   const isHighImpact = useMemo(() => affectedCount !== null && affectedCount > HIGH_IMPACT_THRESHOLD, [affectedCount]);

   const filteredAffectedItems = useMemo(
      () => mappingState.data?.affectedItemNos?.filter((item) => !excludedItems.includes(item)) || [],
      [mappingState.data, excludedItems]
   );

   const systemExcludedItems = useMemo(() => mappingState.data?.excludedItemNos ?? [], [mappingState.data]);

   const fetchMappingData = useCallback(async () => {
      const validationError = validateMaterialGradeId(newMaterialGradeId, currentMaterialGradeId);
      if (validationError) {
         setInputError(validationError);
         return;
      }

      if (!isScopeSelected) {
         setInputError('Please select at least one scope option');
         return;
      }

      if (mappingState.lastFetchedId === newMaterialGradeId && mappingState.data) {
         setHasCheckedMapping(true);
         return;
      }

      setIsLoading(true);
      setError(null);
      setInputError(null);
      setHasCheckedMapping(true);

      try {
         if (scope.type === 'item') {
            setMappingState({
               data: {
                  isAp: false,
                  familyName,
                  materialPart,
                  materialGradeId: parseInt(currentMaterialGradeId || UNASSIGNED_MATERIAL_GRADE_ID),
                  affectedItemNos: [itemNo],
                  excludedItemNos: [],
               },
               lastFetchedId: newMaterialGradeId,
            });
            setIsLoading(false);
            return;
         }

         const response = await APIService.getPiaMaterialMapping({
            itemNo,
            materialPartText: materialGrade,
            doCount: true,
            familyName: scope.type === 'all' ? '' : scope.filters.family ? familyName : '',
            materialPart: scope.type === 'all' ? '' : scope.filters.part ? materialPart : '',
         });

         setMappingState({ data: response, lastFetchedId: newMaterialGradeId });
      } catch (err: any) {
         const errorMessages: Record<number, string> = {
            404: 'Material mapping not found. Please verify the Material Grade ID.',
            500: 'Server error occurred. Please try again later.',
         };

         setError(
            errorMessages[err.response?.status] ||
               (!err.response
                  ? 'Network error. Please check your connection and try again.'
                  : `Failed to fetch material mapping data: ${err.response?.statusText || 'Unknown error'}`)
         );
         console.error('Error fetching mapping data:', err);
      } finally {
         setIsLoading(false);
      }
   }, [newMaterialGradeId, currentMaterialGradeId, itemNo, familyName, materialPart, materialGrade, mappingState, scope, isScopeSelected]);

   const handleItemToggle = useCallback((itemNo: string) => {
      setExcludedItems((prev) => (prev.includes(itemNo) ? prev.filter((id) => id !== itemNo) : [...prev, itemNo]));
   }, []);

   const handleClearAllExcluded = useCallback(() => {
      setExcludedItems([]);
   }, []);

   const handleMaterialGradeIdChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
         const value = e.target.value;
         if (value && !/^\d*$/.test(value)) return;
         if (value.length > MAX_GRADE_ID_LENGTH) return;
         setNewMaterialGradeId(value);
         setInputError(null);
         if (hasCheckedMapping) resetMappingState();
      },
      [hasCheckedMapping, resetMappingState]
   );

   const handleScopeChange = useCallback(
      (field: string, checked: boolean) => {
         setScope((prev) => {
            const newScope = { ...prev, filters: { ...prev.filters } };
            switch (field) {
               case 'item':
                  newScope.type = 'item';
                  newScope.filters.part = false;
                  newScope.filters.family = false;
                  break;
               case 'all':
                  newScope.type = 'all';
                  newScope.filters.part = false;
                  newScope.filters.family = false;
                  break;
               case 'part':
                  if (checked) {
                     newScope.type = 'filters';
                     newScope.filters.part = true;
                  } else {
                     newScope.filters.part = false;
                     newScope.filters.family = false;
                  }
                  break;
               case 'family':
                  if (checked) {
                     newScope.type = 'filters';
                     newScope.filters.family = true;
                  } else {
                     newScope.filters.family = false;
                  }
                  break;
            }
            return newScope;
         });
         resetMappingState();
      },
      [resetMappingState]
   );

   const buttonConfig = useMemo(
      () =>
         getButtonConfig({
            showHighImpactWarning,
            affectedCount,
            isHighImpact,
            isLoading,
            error,
            hasCheckedMapping,
            hasMappingData: !!mappingState.data,
         }),
      [showHighImpactWarning, affectedCount, isHighImpact, isLoading, error, hasCheckedMapping, mappingState.data]
   );

   const handleKeyDownInput = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
         if (e.key === 'Enter') fetchMappingData();
      },
      [fetchMappingData]
   );

   return {
      loading: isLoading,
      mappingState,
      error,
      newMaterialGradeId,
      hasCheckedMapping,
      inputError,
      scope,
      showDetails,
      excludedItems,
      showHighImpactWarning,
      inputRef,
      isScopeSelected,
      totalAffectedCount,
      affectedCount,
      totalExcludedCount,
      isHighImpact,
      buttonConfig,
      filteredAffectedItems,
      systemExcludedItems,
      setShowHighImpactWarning,
      setShowDetails,
      fetchMappingData,
      handleItemToggle,
      handleClearAllExcluded,
      handleMaterialGradeIdChange,
      getScopeDescription: () => getScopeDescription(scope, itemNo, materialGrade, materialPart, familyName),
      handleScopeChange,
      handleKeyDownInput,
   };
};
